<template>
  <div>
    <fieldset>
      <legend
        v-if="showLabel"
        class="text-neutral-900 font-medium lg:font-bold"
      >
        {{ $t('pets.species-label') }}
      </legend>
      <div
        v-if="loading"
        class="w-full flex justify-center items-center py-2"
      >
        <fa
          :icon="['fas', 'bone']"
          class="text-3xl text-ocean-900 fa-spin"
        />
      </div>
      <div
        v-else
        class="divide-y divide-gray-200 lg:divide-y-0 lg:grid lg:grid-cols-2 lg:gap-4 lg:mt-2"
      >
        <vetster-search-checkbox-input
          v-for="species in getPetPrimarySpecies()"
          :key="species.value"
          v-model="selectedSpecies"
          :value="species.value"
          :icon="speciesTreatedIcon(species.value)"
          :label="species.text"
          type="radio"
          :data-testid="`species-btn-${species.value}`"
          @update:modelValue="chooseSpecies(species.value)"
        />
        <vetster-search-checkbox-input
          v-for="species in getPetSecondarySpecies()"
          v-show="showMoreOptions"
          :key="species.value"
          v-model="selectedSpecies"
          :value="species.value"
          :icon="speciesTreatedIcon(species.value)"
          :label="species.text"
          type="radio"
          :data-testid="`species-btn-${species.value}`"
          @update:modelValue="chooseSpecies(species.value)"
        />
      </div>
    </fieldset>
    <button
      v-if="!loading"
      data-testid="species-toggle-btn"
      type="button"
      class="mt-4 flex items-center space-x-2"
      @click="showMoreOptions = !showMoreOptions"
    >
      <span class="text-neutral-900 underline">
        {{ showMoreOptions ? $t('pets.show-less-species') : $t('pets.show-all-species') }}
      </span>
      <span
        :class="{ 'rotate-180': showMoreOptions }"
        class="h-full transition-transform duration-200 ease-in-out"
      >
        <fa
          :icon="['fal', 'chevron-down']"
          class="text-md text-neutral-900 fa-fw"
        />
      </span>
    </button>
  </div>
</template>

<script>
import differenceWith from 'lodash/differenceWith';
import intersectionWith from 'lodash/intersectionWith';
import map from 'lodash/map';
import size from 'lodash/size';
import { mapGetters } from 'vuex';

import VetsterSearchCheckboxInput from '@/brand-components/VetsterSearchCheckboxInput';
import SpeciesTreatedType from '@/enums/SpeciesTreatedType';
import PetIcons from '@/mixins/PetIcons';

export default {
  name: 'VetsterShowPetSpecies',

  components: { VetsterSearchCheckboxInput },

  mixins: [PetIcons],

  props: {
    showLabel: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      loading: true,
      primarySpecies: [
        SpeciesTreatedType.Dog,
        SpeciesTreatedType.Cat,
        SpeciesTreatedType.Bird,
      ],
      selectedSpecies: [],
      showMoreOptions: false,
    };
  },

  computed: {
    ...mapGetters('search', ['getInputs']),
    ...mapGetters('speciesTreated', ['getSpeciesTreated']),
  },

  watch: {
    selectedSpecies(value) {
      this.$store.commit('search/setTempInputs', {
        species_treated: value,
      });
    },
  },

  async created() {
    if (!size(this.getSpeciesTreated)) {
      await this.$store.dispatch('speciesTreated/fetchSpeciesTreated');
    }
    this.loading = false;
  },

  async mounted() {
    if (size(this.getInputs?.species_treated)) {
      this.selectedSpecies = this.getInputs?.species_treated[0];
    }

    if (this.selectedSpecies && !this.primarySpecies.includes(this.selectedSpecies)) {
      this.primarySpecies.unshift(this.selectedSpecies);
    }
  },

  methods: {
    async chooseSpecies(species) {
      this.selectedSpecies = species;
    },

    getPetPrimarySpecies() {
      const primarySpecies = intersectionWith(
        this.primarySpecies,
        map(this.getSpeciesTreated, (species) => species.name),
      );

      return map(primarySpecies, (value) => ({
        value,
        text: this.$t(`pets.species.${value}`),
      }));
    },

    getPetSecondarySpecies() {
      return map(
        differenceWith(map(this.getSpeciesTreated, (species) => species.name), this.primarySpecies),
        (value) => ({
          value,
          text: this.$t(`pets.species.${value}`),
        }),
      );
    },
  },
};
</script>
